
import { Component, OnInit } from "@angular/core";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IDiagnosticTroubleCode } from "@app/main/modules/maintenance/_components/faultCodes/_models/IDiagnosticTroubleCode";

import { LatestSensorDataValues, SensorDataValueType } from "@interfaces/ISensorDataValue";
import { NavigationService } from "@services/navigation.service";
import { VehicleService } from "@services/vehicle.service";
import { MultiStateInputCategory } from "@temperature/_enums/reefer/MultiStateInputCategory.enum";
import { ReeferEngineModeCategory } from "@temperature/_enums/reefer/ReeferEngineModeCategory.enum";
import { ReeferMake } from "@temperature/_enums/reefer/ReeferMake.enum";
import { ReeferOperatingModeCategory } from "@temperature/_enums/reefer/ReeferOperatingModeCategory.enum";
import { IMultiStateInputRecord } from "@temperature/_models/multiStateInputs/IMultiStateInputRecord";
import { IReefer } from "@temperature/_models/reefer/IReefer";
import { IReeferWithMappings } from "@temperature/_models/reefer/IReeferWithMappings";
import { MultiStateInputService } from "@temperature/_services/multi-state-input.service";
import { ReeferService } from "@temperature/_services/reefer.service";
import "./reefer-detail.component.scss";
import { lastValueFrom } from "rxjs";
import { FaultCodesService } from "@app/main/modules/maintenance/_components/faultCodes/fault-codes.service";
import { IDiagnosticRecord } from "@app/main/modules/maintenance/_components/faultCodes/_models/IDiagnosticRecord";
import { SensorDataService } from "@app/main/modules/maintenance/_components/sensorData/sensor-data.service";

@Component({
    selector: "reefer-detail",
    template: require('./reefer-detail.component.html')
})
export class ReeferDetailComponent implements OnInit {
    loading: boolean;

    vehicleId: number;
    vehicle: IVehicle;
    reeferUnit: IReeferWithMappings;

    diagnosticRecords: IDiagnosticRecord[];
    diagnosticTroubleCodes: IDiagnosticTroubleCode[];
    diagnosticErrors: IDiagnosticTroubleCode[];

    latestMultiStateInputRecords: IMultiStateInputRecord[];
    sensorDataValues: LatestSensorDataValues[];
    latestSensorDataValues: LatestSensorDataValues;

    ReeferOperatingModeCategory = ReeferOperatingModeCategory;
    ReeferEngineModeCategory = ReeferEngineModeCategory;
    ReeferMake = ReeferMake;

    constructor(
        private readonly faultCodeService: FaultCodesService,
        private readonly multiStateInputService: MultiStateInputService,
        private readonly navigationService: NavigationService,
        private readonly reeferService: ReeferService,
        private readonly sensorDataService: SensorDataService,
        private readonly vehicleService: VehicleService) {
    }

    ngOnInit(): void {
        this.vehicleId = Number(this.navigationService.getRouteParam('vehicleId'));
        this.getData(true);
    }

    onLastUpdateChanged($event) {
        if (this.reeferUnit) {
            this.reeferUnit.lastUpdated = $event;
        }
    }

    //--

    private getData(first = false) {
        if (first) {
            this.loading = true;
        }

        const reeferPromise: Promise<IReefer[]> = lastValueFrom(this.reeferService.getReeferData({ vehicleId: this.vehicleId }));
        const propertiesPromise = this.getPropertiesForReeferData();

        Promise.all([reeferPromise, propertiesPromise])
            .then(([reeferUnits, _]) => {
                this.reeferUnit = reeferUnits[0];

                this.reeferService.santitiseReeferSerialNumber(this.reeferUnit);

                this.latestSensorDataValues = this.sensorDataValues.find(data => data.vehicleId === this.reeferUnit.VehicleId);
                this.getModes();

                this.diagnosticErrors = this.reeferService.getActiveReeferErrors(this.diagnosticRecords, this.diagnosticTroubleCodes);
                this.reeferUnit.multiStateInputRecords = this.latestMultiStateInputRecords;
            })
            .catch(e => {
                console.error(e);
            })
            .finally(() => {
                this.loading = false;
            })
    }

    private getPropertiesForReeferData() {
        const promises: any[] = [
            this.getVehicleData(),
            this.getDiagnosticData(),
            this.getMultiStateInputData()
        ];

        return Promise.all(promises);
    }

    private getVehicleData() {
        const vehiclePromise = this.vehicleService.getVehicle(this.vehicleId, { includePeripheralMappings: true }) as Promise<IVehicle>;
        const sensorDataPromise = this.sensorDataService.getLatestSensorDataValues({ VehicleId: this.vehicleId });

        return Promise.all([vehiclePromise, sensorDataPromise]).then(([vehicle, sensorDataValues]) => {
            this.vehicle = vehicle;
            this.sensorDataValues = sensorDataValues;
        })
    }

    private getDiagnosticData() {
        const faultCodePromises: any[] = [
            this.faultCodeService.getActiveDiagnosticRecordsForVehicle(this.vehicleId),
            this.faultCodeService.getReeferDiagnosticTroubleCodes()
        ]

        return Promise.all(faultCodePromises).then(([diagnosticRecords, troubleCodes]) => {
            this.diagnosticRecords = this.reeferService.filterReeferErrorCodes(diagnosticRecords);
            this.diagnosticTroubleCodes = troubleCodes;
        })
    }

    private getMultiStateInputData() {
        lastValueFrom(this.multiStateInputService.getLatestMultiStateInputRecords()).then((latestMultiStateInputRecords) => {
            this.latestMultiStateInputRecords = latestMultiStateInputRecords.filter(record => record.VehicleId === this.vehicleId);
        })
    }

    private getModes() {
        const hardwareSource = this.reeferService.getHardwareSource(this.reeferUnit.Make);

        if (this.latestMultiStateInputRecords?.length) {
            this.reeferUnit.operatingMode = this.latestMultiStateInputRecords.find(record => record.Category == MultiStateInputCategory.ReeferOperatingMode && record.Source == hardwareSource)?.State;
            this.reeferUnit.engineMode = this.latestMultiStateInputRecords.find(record => record.Category == MultiStateInputCategory.ReeferEngineMode && record.Source == hardwareSource)?.State;
        }

        this.reeferUnit.voltage = Number(this.latestSensorDataValues?.getValue<number>(SensorDataValueType.PrimaryAuxBatteryVoltage).toFixed(2));
    }
}
